import React, { useEffect, useMemo, useState } from "react";
import Shell from "../components/Shell";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowUpRight, ArrowDownRight, Plus, Sparkles, Target, Bell, X, PiggyBank } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import TransactionDialog from "../components/TransactionDialog";
import SpiderLoader from "../components/SpiderLoader";
import MonthPicker from "../components/MonthPicker";
import { getCategoryMeta } from "../lib/categoryIcons";

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [goals, setGoals] = useState([]);
  const [debtsSummary, setDebtsSummary] = useState(null);
  const [upcomingDebts, setUpcomingDebts] = useState([]);
  const [bigBills, setBigBills] = useState([]);
  const [remindersDismissed, setRemindersDismissed] = useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      return localStorage.getItem("argent-debt-reminders-dismissed") === today;
    } catch { return false; }
  });
  const dismissReminders = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem("argent-debt-reminders-dismissed", today);
    } catch {}
    setRemindersDismissed(true);
  };
  const [open, setOpen] = useState(false);
  const [insight, setInsight] = useState("");
  const [insightLoading, setInsightLoading] = useState(false);
  const [month, setMonth] = useState(currentMonthStr());

  const getInsight = async () => {
    setInsightLoading(true);
    try {
      const r = await api.post("/insights/weekly");
      setInsight(r.data.insight || "");
    } catch { setInsight("Couldn't generate right now. Try again in a moment."); }
    finally { setInsightLoading(false); }
  };

  const budgetColor = (p) => {
    if (p >= 90) return "bg-rose-500";
    if (p >= 75) return "bg-amber-500";
    return "bg-emerald-500";
  };
  const budgetTextColor = (p) => {
    if (p >= 90) return "text-rose-600";
    if (p >= 75) return "text-amber-600";
    return "text-emerald-600";
  };

  const load = async () => {
    const scoped = month && month !== currentMonthStr();
    const summaryParams = scoped ? { month } : {};
    const txParams = scoped ? { month, limit: 6 } : { limit: 6 };
    const [s, t, g, d, bl] = await Promise.all([
      api.get("/analytics/summary", { params: summaryParams }),
      api.get("/transactions", { params: txParams }),
      api.get("/goals"),
      api.get("/debts/projection").catch(() => ({ data: null })),
      api.get("/bills").catch(() => ({ data: null })),
    ]);
    setSummary(s.data);
    setRecent(t.data.slice(0, 6));
    setGoals(g.data || []);

    // ---- Aggregate combined this-month payment status (debts + bills) ----
    const debts = d.data?.debts || [];
    const bills = bl.data?.bills || [];
    const hasDebts = debts.length > 0;
    const hasBills = bills.length > 0;

    let totalPaid = 0;
    let totalMinimum = 0;
    let itemsPaid = 0;
    for (const dd of debts) {
      const ptm = dd.paid_this_month || {};
      totalPaid += ptm.amount || 0;
      totalMinimum += ptm.minimum || 0;
      if (ptm.is_paid) itemsPaid += 1;
    }
    for (const bb of bills) {
      const ptm = bb.paid_this_month || {};
      totalPaid += ptm.amount || 0;
      totalMinimum += bb.amount || 0;
      if (ptm.is_paid) itemsPaid += 1;
    }
    if (hasDebts || hasBills) {
      setDebtsSummary({
        total_paid: Math.round(totalPaid * 100) / 100,
        total_minimum: Math.round(totalMinimum * 100) / 100,
        debts_paid: itemsPaid,
        debts_total: debts.length + bills.length,
        has_bills: hasBills,
        has_debts: hasDebts,
      });
    } else {
      setDebtsSummary(null);
    }

    // ---- Autopay reminders: unpaid debts + bills due today/tomorrow ----
    const soonDebts = debts.filter(dd => {
      const nd = dd.next_due;
      const paid = dd.paid_this_month?.is_paid;
      return nd && !paid && nd.days_until != null && nd.days_until <= 1;
    }).map(dd => ({
      key: `debt-${dd.id}`,
      name: dd.name,
      amount: dd.minimum_payment || 0,
      days_until: dd.next_due.days_until,
      kind: "debt",
    }));
    const soonBills = bills.filter(bb => {
      const nd = bb.next_due;
      const paid = bb.paid_this_month?.is_paid;
      return nd && !paid && nd.days_until != null && nd.days_until <= 1;
    }).map(bb => ({
      key: `bill-${bb.id}`,
      name: bb.name,
      amount: bb.amount || 0,
      days_until: bb.next_due.days_until,
      kind: "bill",
    }));
    setUpcomingDebts([...soonDebts, ...soonBills]);

    // Big bills strip: quarterly/annual bills that hit within the next 30 days
    // and haven't been paid yet. Sorted by soonest.
    const big = bills
      .filter(bb => {
        const f = bb.frequency || "monthly";
        const nd = bb.next_due;
        const paid = bb.paid_this_month?.is_paid;
        return (f === "quarterly" || f === "annually")
          && nd && !paid
          && nd.days_until != null
          && nd.days_until >= 0
          && nd.days_until <= 30;
      })
      .sort((a, b) => (a.next_due.days_until ?? 0) - (b.next_due.days_until ?? 0));
    setBigBills(big);
  };
  useEffect(() => { load(); }, [month]);

  const cur = user?.currency || "USD";
  const budget = summary?.monthly_budget || 0;
  const spent = summary?.month_expense || 0;
  const pct = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
  const remainingPct = Math.max(0, 100 - pct);
  const isCurrentMonth = summary?.is_current_month !== false;
  const monthLabel = summary?.month_label || "This Month";

  return (
    <Shell>
      {/* Month picker header */}
      <div className="mb-4 sm:mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
            {isCurrentMonth ? "This Month" : "Viewing"}
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
            {monthLabel}
          </div>
        </div>
        <MonthPicker value={month} onChange={setMonth} testId="dashboard-month-picker" />
      </div>

      {/* Autopay reminder banner */}
      {isCurrentMonth && !remindersDismissed && upcomingDebts.length > 0 && (() => {
        const totalDue = upcomingDebts.reduce((s, d) => s + (d.amount || 0), 0);
        const anyToday = upcomingDebts.some(d => d.days_until === 0);
        const hasBills = upcomingDebts.some(d => d.kind === "bill");
        const hasDebts = upcomingDebts.some(d => d.kind === "debt");
        // Prefer the page that has items due; if both, link to whichever is first
        const linkTo = hasBills && !hasDebts ? "/bills" : "/debts";
        return (
          <div
            data-testid="autopay-reminder-banner"
            className={`mb-4 sm:mb-6 flex items-start gap-3 rounded-3xl px-4 sm:px-5 py-3 sm:py-4 border ${
              anyToday
                ? "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 text-rose-800 dark:text-rose-200"
                : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-900 dark:text-amber-200"
            }`}
          >
            <div className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${
              anyToday ? "bg-rose-100 dark:bg-rose-500/20" : "bg-amber-100 dark:bg-amber-500/20"
            }`}>
              <Bell className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">
                {anyToday
                  ? `${upcomingDebts.length === 1 ? "1 payment is" : `${upcomingDebts.length} payments are`} due today — total ${fmtMoney(totalDue, cur)}`
                  : `${upcomingDebts.length === 1 ? "1 payment is" : `${upcomingDebts.length} payments are`} due tomorrow — total ${fmtMoney(totalDue, cur)}`}
              </div>
              <div className="mt-1 text-xs opacity-90 flex flex-wrap items-center gap-x-2 gap-y-1">
                {upcomingDebts.map((dd, i) => (
                  <span key={dd.key} className="inline-flex items-center gap-1">
                    {i > 0 && <span className="opacity-40">·</span>}
                    <span className="font-semibold">{dd.name}</span>
                    <span className="tabular opacity-80">{fmtMoney(dd.amount || 0, cur)}</span>
                    <span className="text-[10px] uppercase tracking-widest opacity-70">
                      {dd.kind === "bill" ? "bill" : "debt"} · {dd.days_until === 0 ? "today" : "tomorrow"}
                    </span>
                  </span>
                ))}
              </div>
              <Link
                to={linkTo}
                className="mt-2 inline-block text-xs font-bold underline underline-offset-2"
                data-testid="autopay-reminder-link"
              >
                {hasBills && hasDebts ? "Review payments →" : hasBills ? "Log bill payment →" : "Log payment →"}
              </Link>
            </div>
            <button
              onClick={dismissReminders}
              className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              aria-label="Dismiss reminder for today"
              data-testid="autopay-reminder-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })()}

      {/* Upcoming Big Bills — quarterly/annual bills due in next 30 days */}
      {isCurrentMonth && bigBills.length > 0 && (() => {
        const totalDue = bigBills.reduce((s, b) => s + (b.amount || 0), 0);
        return (
          <div
            data-testid="big-bills-strip"
            className="mb-4 sm:mb-6 rounded-3xl border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/70 dark:bg-indigo-500/10 p-4 sm:p-5"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-2xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 grid place-items-center">
                <PiggyBank className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-indigo-700 dark:text-indigo-300">
                  Heads up · Big bill{bigBills.length !== 1 ? "s" : ""} incoming
                </div>
                <div className="mt-1 text-sm font-bold text-slate-900 dark:text-slate-100">
                  {bigBills.length === 1
                    ? <>{bigBills[0].name} · <span className="tabular">{fmtMoney(bigBills[0].amount, cur)}</span> in {bigBills[0].next_due.days_until} day{bigBills[0].next_due.days_until !== 1 ? "s" : ""}</>
                    : <>{bigBills.length} bills · total <span className="tabular">{fmtMoney(totalDue, cur)}</span> in the next 30 days</>}
                </div>
                <div className="mt-3 space-y-2">
                  {bigBills.slice(0, 3).map(b => {
                    // Rough "set aside per remaining week" nudge
                    const weeks = Math.max(1, Math.ceil((b.next_due.days_until || 0) / 7));
                    const perWeek = Math.round((b.amount || 0) / weeks);
                    return (
                      <div key={b.id} className="flex items-center justify-between gap-3 bg-white dark:bg-slate-900/60 rounded-2xl px-3 py-2.5" data-testid={`big-bill-row-${b.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                            {b.name}
                            <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-indigo-600 dark:text-indigo-400">
                              {b.frequency === "quarterly" ? "Quarterly" : "Annually"}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            {new Date(b.next_due.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                            {" · "}
                            in {b.next_due.days_until} day{b.next_due.days_until !== 1 ? "s" : ""}
                            {" · set aside "}
                            <span className="tabular font-semibold text-slate-800 dark:text-slate-200">
                              {fmtMoney(perWeek, cur)}/week
                            </span>
                          </div>
                        </div>
                        <div className="tabular text-sm font-bold text-slate-900 dark:text-slate-100 shrink-0">
                          {fmtMoney(b.amount, cur)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Link
                  to="/bills"
                  className="mt-3 inline-block text-xs font-bold text-indigo-700 dark:text-indigo-300 hover:underline"
                  data-testid="big-bills-link"
                >
                  Manage bills →
                </Link>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-12 gap-4 sm:gap-6">
        {/* Balance Hero */}
        <div className="col-span-12 lg:col-span-8 spidey-hero web-pattern text-white rounded-3xl p-5 sm:p-8 relative overflow-hidden">
          <div className="flex justify-between items-start gap-3 relative">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-white/70">
                {isCurrentMonth ? "Current Balance" : "Balance"}
              </div>
              <div className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold mt-2 tabular break-words" data-testid="balance-value">
                {fmtMoney(summary?.balance || 0, cur)}
              </div>
              <div className="flex gap-4 sm:gap-6 mt-4 sm:mt-6">
                <div>
                  <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-white/60">In {!isCurrentMonth && `· ${monthLabel.split(" ")[0]}`}</div>
                  <div className="text-emerald-300 font-semibold tabular text-sm sm:text-base">{fmtMoney(summary?.month_income || 0, cur)}</div>
                </div>
                <div>
                  <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-white/60">Out {!isCurrentMonth && `· ${monthLabel.split(" ")[0]}`}</div>
                  <div className="text-rose-200 font-semibold tabular text-sm sm:text-base">{fmtMoney(summary?.month_expense || 0, cur)}</div>
                </div>
              </div>
            </div>
            <Button data-testid="add-transaction-btn" onClick={() => setOpen(true)} className="rounded-full !bg-white !text-[#DC1F26] hover:!bg-slate-100 font-bold shrink-0 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm">
              <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">New</span>
            </Button>
          </div>
        </div>

        {/* Budget Card */}
        <div className="col-span-12 lg:col-span-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
              {isCurrentMonth ? "Monthly Budget" : `${monthLabel} Spend`}
            </div>
            {isCurrentMonth && (
              <span className={`text-xs font-bold ${budgetTextColor(pct)}`}>{remainingPct}% left</span>
            )}
          </div>
          <div className="font-display text-2xl sm:text-3xl font-bold mt-3 tabular text-slate-900 dark:text-slate-100 break-words" data-testid="budget-spent">
            {fmtMoney(spent, cur)} <span className="text-slate-400 text-base sm:text-lg font-medium">/ {fmtMoney(budget, cur)}</span>
          </div>
          <div className="mt-4 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${budgetColor(pct)}`} style={{ width: `${pct}%` }}></div>
          </div>
          <Link to="/settings" className="mt-4 inline-block text-xs font-semibold spidey-red hover:underline">Adjust budget →</Link>
        </div>

        {/* Debts Paid This Month */}
        {debtsSummary && debtsSummary.debts_total > 0 && (() => {
          const pct = debtsSummary.total_minimum > 0
            ? Math.min(100, Math.round((debtsSummary.total_paid / debtsSummary.total_minimum) * 100))
            : 0;
          const remaining = Math.max(0, debtsSummary.total_minimum - debtsSummary.total_paid);
          const allCovered = debtsSummary.debts_paid === debtsSummary.debts_total;
          const barColor = allCovered ? "bg-emerald-500" : pct >= 75 ? "bg-amber-500" : "bg-spidey-red";
          const hasDebts = debtsSummary.has_debts;
          const hasBills = debtsSummary.has_bills;
          const label = hasDebts && hasBills
            ? "Paid this month · debts + bills"
            : hasBills
            ? "Bills paid this month"
            : "Debts paid this month";
          const itemNoun = hasDebts && hasBills ? "payment" : hasBills ? "bill" : "debt";
          const linkTo = hasBills && !hasDebts ? "/bills" : "/debts";
          const linkLabel = hasBills && !hasDebts
            ? "View bills →"
            : hasDebts && hasBills
            ? "View →"
            : "View debts →";
          return (
            <div className="col-span-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6" data-testid="debts-paid-tile">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
                    {label}
                  </div>
                  <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-2xl sm:text-3xl font-bold tabular text-slate-900 dark:text-slate-100" data-testid="debts-paid-amount">
                      {fmtMoney(debtsSummary.total_paid, cur)}
                    </span>
                    <span className="text-sm text-slate-400 dark:text-slate-500 font-medium">
                      of {fmtMoney(debtsSummary.total_minimum, cur)} {hasDebts && !hasBills ? "minimum" : "due"}
                    </span>
                  </div>
                  <div className="mt-2 text-xs sm:text-sm">
                    {allCovered ? (
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        ✅ All {debtsSummary.debts_total} {itemNoun}{debtsSummary.debts_total !== 1 ? "s" : ""} covered this month — nice work
                      </span>
                    ) : (
                      <span className="text-slate-600 dark:text-slate-400">
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{debtsSummary.debts_paid}</span> of {debtsSummary.debts_total} covered
                        {remaining > 0 && (
                          <>
                            {" · "}
                            <span className="font-semibold text-rose-600 dark:text-rose-400 tabular">
                              {fmtMoney(remaining, cur)}
                            </span>
                            {" still owed to avoid late fees"}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={linkTo}
                  className="text-xs font-semibold spidey-red hover:underline shrink-0"
                  data-testid="debts-paid-tile-link"
                >
                  {linkLabel}
                </Link>
              </div>
              <div className="mt-4 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* Recent Transactions */}
        <div className="col-span-12 lg:col-span-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4 gap-2">
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 truncate">
                {isCurrentMonth ? "Recent Transactions" : `${monthLabel} Transactions`}
              </h3>
              {!isCurrentMonth && (
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Showing this month's activity only</div>
              )}
            </div>
            <Link to="/transactions" className="text-xs font-semibold spidey-red hover:underline shrink-0">View all →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="py-12 text-center">
              <Sparkles className="w-6 h-6 text-slate-300 mx-auto" />
              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {isCurrentMonth ? "No transactions yet. Add your first one." : `No transactions in ${monthLabel}.`}
              </div>
              {isCurrentMonth && (
                <Button onClick={() => setOpen(true)} className="mt-4 rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="empty-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add transaction
                </Button>
              )}
            </div>
          ) : (
            <div>
              {recent.map(t => {
                const meta = getCategoryMeta(t.category);
                const CatIcon = meta.Icon;
                return (
                <div key={t.id} data-testid={`recent-tx-${t.id}`} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-none">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center ${meta.bg} ${meta.fg}`}>
                      <CatIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t.category}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{t.description || new Date(t.date).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 text-sm font-semibold tabular ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.type === "income" ? <ArrowUpRight className="w-3.5 h-3.5"/> : <ArrowDownRight className="w-3.5 h-3.5"/>}
                    {t.type === "income" ? "+" : "-"}{fmtMoney(t.amount, cur)}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Categories */}
        <div className="col-span-12 lg:col-span-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6">
          <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Top Categories</h3>
          {(summary?.categories || []).slice(0, 5).length === 0 ? (
            <div className="py-6 text-sm text-slate-500 dark:text-slate-400">No expenses this month.</div>
          ) : (
            <div className="space-y-4">
              {(summary?.categories || []).slice(0, 5).map((c, i) => {
                const total = summary?.month_expense || 1;
                const p = Math.round((c.amount / total) * 100);
                return (
                  <div key={c.category}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{c.category}</div>
                      <div className="tabular text-slate-500 dark:text-slate-400">{fmtMoney(c.amount, cur)} · {p}%</div>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-spidey-red rounded-full" style={{ width: `${p}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Savings Goals Summary */}
        <div className="col-span-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-900/40 grid place-items-center">
                <Target className="w-5 h-5 text-emerald-700 dark:text-emerald-300" />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Savings Goals</div>
                <div className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">Target Progress</div>
              </div>
            </div>
            <Link to="/goals" className="text-xs font-semibold spidey-red hover:underline" data-testid="goals-view-all">
              {goals.length > 0 ? "Manage all →" : "Create goal →"}
            </Link>
          </div>

          {goals.length === 0 ? (
            <div className="py-8 text-center">
              <div className="text-sm text-slate-500 dark:text-slate-400">No savings goals yet. Set one to start tracking progress.</div>
              <Link to="/goals" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-semibold" data-testid="goals-create-empty">
                <Plus className="w-3.5 h-3.5" /> Create your first goal
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {goals.slice(0, 3).map(g => {
                const pct = g.target_amount > 0 ? Math.min(100, Math.round((g.saved_amount / g.target_amount) * 100)) : 0;
                const remaining = Math.max(0, g.target_amount - g.saved_amount);
                const days = Math.ceil((new Date(g.deadline) - new Date()) / (1000 * 60 * 60 * 24));
                const done = pct >= 100;
                return (
                  <div key={g.id} data-testid={`dash-goal-${g.id}`} className="border border-slate-200 dark:border-slate-800 rounded-2xl p-4 bg-slate-50/50 dark:bg-slate-800/40">
                    <div className="flex justify-between items-start gap-2">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{g.name}</div>
                      <div className={`text-xs font-bold shrink-0 ${done ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`}>{pct}%</div>
                    </div>
                    <div className="mt-3 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-spidey-red"}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs mt-3">
                      <div className="tabular text-slate-600 dark:text-slate-400">
                        <span className="font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(g.saved_amount, cur)}</span> / {fmtMoney(g.target_amount, cur)}
                      </div>
                      <div className="text-slate-500 dark:text-slate-400">
                        {done ? "Done!" : days > 0 ? `${days}d left` : "Overdue"}
                      </div>
                    </div>
                    {!done && (
                      <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                        {fmtMoney(remaining, cur)} to go
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Insight Coach */}
        <div className="col-span-12 spidey-hero web-pattern text-white rounded-3xl p-5 sm:p-6 relative overflow-hidden">
          <div className="flex items-start justify-between gap-4 flex-wrap relative">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur grid place-items-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase font-bold text-white/70">Insight Coach</div>
                <div className="font-display text-xl font-semibold text-white">Your weekly recap</div>
              </div>
            </div>
            <Button
              onClick={getInsight}
              disabled={insightLoading}
              data-testid="get-insight-btn"
              className="rounded-full !bg-white !text-[#DC1F26] hover:!bg-slate-100 font-bold"
            >
              {insightLoading ? <span className="inline-flex items-center gap-2"><span className="w-5 h-5 spidey-red"><SpiderLoader size={20} /></span> Thinking…</span> : <><Sparkles className="w-4 h-4 mr-1" /> {insight ? "Refresh" : "Generate recap"}</>}
            </Button>
          </div>
          {insight && (
            <div data-testid="insight-text" className="mt-5 text-sm leading-relaxed text-white/90 whitespace-pre-line rise relative">
              {insight}
            </div>
          )}
          {!insight && !insightLoading && (
            <div className="mt-5 text-sm text-white/80 relative">Tap the button for a friendly AI-generated look at where your money went — and one small tweak to try next week.</div>
          )}
        </div>
      </div>

      <TransactionDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </Shell>
  );
}
